import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { DashboardFeature } from 'src/app/models/dashboard-common.model';
import { AvailabilityInput, FloorBookingInput, TeamRequestBookingInput, TimeSlotType } from 'src/app/models/find-a-space.model';
import {
  AuditTrailInput,
  AuditTrailResponse,
  BOOKING_AUDIT_TRAIL_PROPS,
  BookingBySpaceRequest,
  BookingDetails,
  BookingsDashboardInput,
  BookingsDashboardResponse,
  DetailsResponse,
  ReleaseSpaceBookingInput,
  ReservationServiceOrderCount,
  ServiceOrdersCount,
  WaitlistDashboardData,
  WaitListDashboardRequest,
  EditBooking,
  BOOKING_AUDIT_TRAIL_PROPS_NAMES
} from '../../../models/bookings.model';
import { DataContextService } from '../../datacontext.service';
import * as moment from 'moment';
import { IdWithRevision, SpaceFeature } from '../../../models/masterdata.model';
import { ReservationDetails } from '../../../models/meetings.model';
import { ReferenceType } from '../../../models/common.enums';
import { ReservationFeatureFlag } from '../../../models/reservations/reservations.models';

@Injectable({
  providedIn: 'root'
})
export class BookingsService {

  constructor(private dataContextService: DataContextService) {
  }

  private bookingDetails = new BehaviorSubject<BookingDetails>(null);
  bookingDetails$ = this.bookingDetails.asObservable();
  private detailsCache: Map<string, DetailsResponse> = new Map();
  releaseBooking(input: ReleaseSpaceBookingInput[]) {
    const url = `SpaceBookings`;
    return this.dataContextService.deleteData(url, input).pipe(
      map(res => {
        if (res.status == 200) {
          return res.data
        } else {
          this.dataContextService.showErrorToaster(res);
        }
      })
    );
  }

  noShowBooking(idWithRevision: IdWithRevision[]) {
    const noShowBookings: ReleaseSpaceBookingInput[] = [];
    idWithRevision.forEach(val => {
      const input: ReleaseSpaceBookingInput = {
        statusComment: null,
        statusReasonId: 3,
        id: val.id,
        revision: val.revision,
      };
      noShowBookings.push(input)
    });

    return this.releaseBooking(noShowBookings).pipe(map((res) => {
      return res;
    }));
  }

  getBookingsBySpace(bookingBySpace: BookingBySpaceRequest) {
    let url = `SpaceBookings/BySpaces?`;
    if (bookingBySpace.startDate) {
      url += `StartDate=${encodeURIComponent(bookingBySpace.startDate)}&`;
    }
    if (bookingBySpace.endDate) {
      url += `EndDate=${encodeURIComponent(bookingBySpace.endDate)}&`;
    }
    if (bookingBySpace.spaceIds.length > 0) {
      bookingBySpace.spaceIds.forEach((id) => {
        url += `SpaceIds=${id}&`;
      });
    }
    url = url.slice(0, -1);
    return this.dataContextService.getData(url, true, false, true).pipe(map((res) => {
      if (res.status == 200) {
        if (res.data && res.data.length) {
          const data = res.data as BookingDetails[];
          data.forEach(response => {
            if (response.owner) {
              response.owner.firstName = this.dataContextService.getUserFirstName(response.owner);
              response.owner.lastName = response.owner.lastName ? response.owner.lastName : '';
            }
            if (response.primaryContact) {
              response.primaryContact.firstName = this.dataContextService.getUserFirstName(response.primaryContact);
              response.primaryContact.lastName = response.primaryContact.lastName ? response.primaryContact.lastName : '';
            }
            if (response.specialist) {
              response.specialist.firstName = this.dataContextService.getUserFirstName(response.specialist);
              response.specialist.lastName = response.specialist.lastName ? response.specialist.lastName : '';
            }
            if (response.createBy) {
              response.createBy.firstName = this.dataContextService.getUserFirstName(response.createBy);
              response.createBy.lastName = response.createBy.lastName ? response.createBy.lastName : '';
            }
          })
          return data;
        }
      } else {
        this.dataContextService.showErrorToaster(res);
      }
    }));
  }

  getFloorBookings(input: FloorBookingInput): Observable<BookingDetails[]> {
    let url = `Floors/${input.floorUniqueKey}/SpaceBookings?StartDate=${encodeURIComponent(input.startDate)}&EndDate=${encodeURIComponent(
      input.endDate
    )}`;
    if (!!input.userId) {
      url += `&UserId=${input.userId}`;
    }
    return this.dataContextService.getData(url).pipe(map(res => {
      if (res.status == 200) {
        const data = res.data as BookingDetails[];
        data.forEach(response => {
          if (response.owner) {
            response.owner.firstName = this.dataContextService.getUserFirstName(response.owner);
            response.owner.lastName = response.owner.lastName ? response.owner.lastName : '';
          }
          if (response.primaryContact) {
            response.primaryContact.firstName = this.dataContextService.getUserFirstName(response.primaryContact);
            response.primaryContact.lastName = response.primaryContact.lastName ? response.primaryContact.lastName : '';
          }
          if (response.specialist) {
            response.specialist.firstName = this.dataContextService.getUserFirstName(response.specialist);
            response.specialist.lastName = response.specialist.lastName ? response.specialist.lastName : '';
          }
          if (response.createBy) {
            response.createBy.firstName = this.dataContextService.getUserFirstName(response.createBy);
            response.createBy.lastName = response.createBy.lastName ? response.createBy.lastName : '';
          }
        })
        return data;
      } else {
        this.dataContextService.showErrorToaster(res);
      }
    }));
  }

  getWaitListDashboardData(obj: WaitListDashboardRequest, showGlobalSpinner: boolean = true) {
    let dashboardRequest = {
      limit: obj.Limit,
      page: obj.Page,
      term: obj.Term,
      workspaceWaitListBuildingSearchCriterias: obj.WorkspaceWaitListBuildingSearchCriterias,
      spaceTypeSearchCriteria: {
        any: obj.SpaceTypeSearchCriteriaAny != null ? obj.SpaceTypeSearchCriteriaAny : true,
        workstation: obj.SpaceTypeSearchCriteriaWorkstation != null ? obj.SpaceTypeSearchCriteriaWorkstation : true,
        privateSpace: obj.SpaceTypeSearchCriteriaPrivateSpace != null ? obj.SpaceTypeSearchCriteriaPrivateSpace : true
      },
      workSpaceWaitListSortings: obj.WorkSpaceWaitListSortings,
      employeeLevels: obj.EmployeeLevels,
      waitListStatusTypes: obj.Status,
      features: DashboardFeature.IncludeTotalCount,
      startDate: obj.StartDate,
      endDate: obj.EndDate
    };
    dashboardRequest = this.dataContextService.removeNullValuesFromObject(dashboardRequest);
    return this.dataContextService.postData(`WorkspaceWaitLists/Dashboard`, dashboardRequest, showGlobalSpinner).pipe(map((res) => {
      if (res.status == 200) {
        const response = res.data as WaitlistDashboardData;
        response.items.forEach(i => {
          if (i.owner) {
            i.owner.firstName = this.dataContextService.getUserFirstName(i.owner);
            i.owner.lastName = i.owner.lastName ? i.owner.lastName : '';
          }
        })
        return response;
      }
      else {
        return this.dataContextService.showErrorToaster(res);
      }
    }));
  }

  teamRequestBookings(params: Partial<AvailabilityInput>, teamRequestId: string): Observable<BookingDetails[]> {
    let payload: TeamRequestBookingInput;

    if (params.eventTime.timeSlotType == TimeSlotType.SingleDay) {
      payload = {
        teamRequestId: teamRequestId,
        dateInterval: {
          startDate: params.eventTime.singleDayEventData.startDate,
          endDate: params.eventTime.singleDayEventData.endDate
        }
      };
    } else {
      payload = {
        teamRequestId: teamRequestId,
        recurrenceRule: {
          recurrencePatternType: params.eventTime.recurrentEventData.recurrenceRule.recurrencePatternType,
          startDate: params.eventTime.recurrentEventData.recurrenceRule.startDate,
          endDate: params.eventTime.recurrentEventData.recurrenceRule.endDate,
          interval: params.eventTime.recurrentEventData.recurrenceRule.interval,
          occurrences: params.eventTime.recurrentEventData.recurrenceRule.occurrences,
          weeklyRecurrencePattern: null
        }
      };
      if (params.eventTime.recurrentEventData.recurrenceRule.weeklyRecurrencePattern) {
        payload.recurrenceRule.weeklyRecurrencePattern = {
          daysOfWeek: params.eventTime.recurrentEventData.recurrenceRule.weeklyRecurrencePattern.daysOfWeek
        };
      }

    }
    ;
    this.dataContextService.removeNullValuesFromObject(payload);

    return this.getTeamRequestBookings(payload);
  }

  getTeamRequestBookings(input: TeamRequestBookingInput): Observable<BookingDetails[]> {
    let url = `SpaceBookings/ByTeamRequest?TeamRequestId=${input.teamRequestId}&`;
    if (input.dateInterval) {
      url += `DateInterval.EndDate=${encodeURIComponent(input.dateInterval.endDate)}&`;
      url += `DateInterval.StartDate=${encodeURIComponent(input.dateInterval.startDate)}&`;
    } else {
      url += `RecurrenceRule.RecurrencePatternType=${input.recurrenceRule.recurrencePatternType}&`;
      url += `RecurrenceRule.EndDate=${encodeURIComponent(input.recurrenceRule.endDate)}&`;
      url += `RecurrenceRule.StartDate=${encodeURIComponent(input.recurrenceRule.startDate)}&`;
      if (input.recurrenceRule.interval) {
        url += `RecurrenceRule.Interval=${input.recurrenceRule.interval}&`;
      }
      if (input.recurrenceRule.occurrences) {
        url += `RecurrenceRule.Occurrences=${input.recurrenceRule.occurrences}&`;
      }
      if (input.recurrenceRule.weeklyRecurrencePattern && input.recurrenceRule.weeklyRecurrencePattern.daysOfWeek && input.recurrenceRule.weeklyRecurrencePattern.daysOfWeek.length > 0) {
        input.recurrenceRule.weeklyRecurrencePattern.daysOfWeek.forEach((day) => {
          url += `RecurrenceRule.WeeklyRecurrencePattern.DaysOfWeek=${day}&`;
        });
      }
    }

    return this.dataContextService.getData(url).pipe(map(res => {
      if (res.status == 200) {
        return res.data as BookingDetails[];
      } else {
        this.dataContextService.showErrorToaster(res);
      }
      ;
    }));
  };

  getBookingsDashboardData(payload: BookingsDashboardInput, showGloablSpinner: boolean = true): Observable<BookingsDashboardResponse> {
    const url = `SpaceBookings/Dashboard`;
    return this.dataContextService.postData(url, payload, showGloablSpinner).pipe(
      map(res => {
        if (res.status === 200) {
          const response = res.data as BookingsDashboardResponse;
          response.items.forEach(i => {
            if (i.spaceBookingSpecialist) {
              i.spaceBookingSpecialist.firstName = this.dataContextService.getUserFirstName(i.spaceBookingSpecialist);
              i.spaceBookingSpecialist.lastName = i.spaceBookingSpecialist.lastName ? i.spaceBookingSpecialist.lastName : '';
            }
            if (i.pointOfContact) {
              i.pointOfContact.firstName = this.dataContextService.getUserFirstName(i.pointOfContact);
              i.pointOfContact.lastName = i.pointOfContact.lastName ? i.pointOfContact.lastName : '';
            }
            if (i.owner) {
              i.owner.firstName = this.dataContextService.getUserFirstName(i.owner);
              i.owner.lastName = i.owner.lastName ? i.owner.lastName : '';
            }
          })
          return response;
        }
        else {
          return this.dataContextService.showErrorToaster(res);
        }
      })
    );
  }

  updateBookingSpecialist(bookingId: number, specialistId: number) {
    const url = `SpaceBookings/${bookingId}/Specialist`;
    return this.dataContextService.patchData(url, { specialistId: specialistId }).pipe(
      map(res => {
        if (res.status == 200) {
          return res.data
        } else {
          this.dataContextService.showErrorToaster(res);
        }
      })
    );
  }

  checkInBooking(bookingId: number) {
    const url = `SpaceBookings/${bookingId}/Checkin`;
    return this.dataContextService.patchData(url, {}).pipe(
      map(res => {
        if (res.status) {
          return res.data
        } else {
          this.dataContextService.showErrorToaster(res);
        }
      })
    );
  }

  snoozeBooking(bookingId: number, startTime: string) {
    const url = `SpaceBookings/${bookingId}/Snooze`;
    return this.dataContextService.patchData(url, { startTime: startTime }).pipe(
      map(res => {
        if (res.status == 200) {
          return res.data;
        } else {
          this.dataContextService.showErrorToaster(res);
        }
      })
    );
  }

  getAutoreleaseTime(bumpTime: Date, facilityCurrentTime: Date) {
    const totalMinutes = Math.floor(Math.floor((bumpTime.getTime() - facilityCurrentTime.getTime()) / 1000) / 60);
    const diffTime = Math.abs(bumpTime.getTime() - facilityCurrentTime.getTime());
    let seconds = Math.floor(diffTime / 1000);
    let minutes = Math.floor(seconds / 60);
    let hours = Math.floor(minutes / 60);
    seconds = seconds % 60;
    minutes = seconds >= 30 ? minutes + 1 : minutes;
    minutes = minutes % 60;
    hours = hours % 24;
    return {
      formattedTime: `${bumpTime > facilityCurrentTime ? '' : '-'}${this.padTo2Digits(hours)} hr ${this.padTo2Digits(minutes)} min`,
      time: `${this.padTo2Digits(hours)}:${this.padTo2Digits(minutes)}:${this.padTo2Digits(seconds)}`,
      totalMinutes
    };
  }

  private padTo2Digits(num) {
    return num.toString().padStart(2, '0');
  }

  getBookingById(id: number, showSpinner?: boolean): Observable<BookingDetails> {
    const url = `SpaceBookings/${id}`;
    return this.dataContextService.getData(url, showSpinner).pipe(
      tap(res => this.bookingDetails.next(res.data)),
      map(i => {
        if (i.status === 200) {
          const response = i.data as BookingDetails;
          if (response.owner) {
            response.owner.firstName = this.dataContextService.getUserFirstName(response.owner);
            response.owner.lastName = response.owner.lastName ? response.owner.lastName : '';
          }
          if (response.primaryContact) {
            response.primaryContact.firstName = this.dataContextService.getUserFirstName(response.primaryContact);
            response.primaryContact.lastName = response.primaryContact.lastName ? response.primaryContact.lastName : '';
          }
          if (response.specialist) {
            response.specialist.firstName = this.dataContextService.getUserFirstName(response.specialist);
            response.specialist.lastName = response.specialist.lastName ? response.specialist.lastName : '';
          }
          if (response.createBy) {
            response.createBy.firstName = this.dataContextService.getUserFirstName(response.createBy);
            response.createBy.lastName = response.createBy.lastName ? response.createBy.lastName : '';
          }
          return response;
        }
        else {
          return this.dataContextService.showErrorToaster(i);
        }
      })
    );
  }

  calculateBookingReservationTime(setupTime: string, tearDownTime: string, startDate: string, endDate: string) {
    //temporary change to handle null in setup and teardown time
    const splitedSetupTime = setupTime ? setupTime.split(':') : ['00', '00'];
    const splitedTearDownTime =  tearDownTime ? tearDownTime.split(':') : ['00', '00'];
    const calStartDate = moment(moment.parseZone(startDate).format('l LT'));
    const calEndDate = moment(moment.parseZone(endDate).format('l LT'));
    startDate = calStartDate.format('YYYY-MM-DDTHH:mm:ss');
    endDate = calEndDate.format('YYYY-MM-DDTHH:mm:ss');
    if (splitedSetupTime[0] != '00') {
      startDate = calStartDate
        .subtract(Number(splitedSetupTime[0]), 'hours')
        .format('YYYY-MM-DDTHH:mm:ss');
    }
    if (splitedSetupTime[1] != '00') {
      startDate = calStartDate
        .subtract(Number(splitedSetupTime[1]), 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss');
    }
    if (splitedTearDownTime[0] != '00') {
      endDate = calEndDate
        .add(Number(splitedTearDownTime[0]), 'hours').format('YYYY-MM-DDTHH:mm:ss');
    }
    if (splitedTearDownTime[1] != '00') {
      endDate = calEndDate
        .add(Number(splitedTearDownTime[1]), 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss');
    }
    return {
      startDate: startDate,
      endDate: endDate,
      isMidnightSpanning: !moment(startDate).isSame(moment(endDate), 'D')
    };
  }

  getFeaturesBySpaceId(spaceId: number): Observable<SpaceFeature[]> {
    const url = `Spaces/${spaceId}/Features`;
    return this.dataContextService.getData(url).pipe(
      map(res => {
        if (res.status == 200) {
          const features = res.data.map(i => {
            const feature: SpaceFeature = {
              id: i.id,
              name: i.name,
              iconKey: i.iconKey,
              iconClass: i.iconKey && DataContextService.SpaceFeatures.find(f => f.iconKey === i.iconKey).iconClass
            };
            return feature;
          });
          return features;
        } else {
          this.dataContextService.showErrorToaster(res);
        }
      })
    );
  }

  getBookingDetails(id: number): Observable<DetailsResponse> {
    if (this.detailsCache.get(id.toString())) {
      return of(this.detailsCache.get(id.toString()));
    }
    else {
      return this.getBookingById(id).pipe(
        switchMap(res => {
          return forkJoin([
            this.getReservationDetails(res.reservationId, `${ReservationFeatureFlag.IncludeSpaceBookings},${ReservationFeatureFlag.IncludeVirtualBookings},${ReservationFeatureFlag.IncludeMeetingWaitLists},${ReservationFeatureFlag.IncludeAllowedStatusActions}`),
            this.getFeaturesBySpaceId(res.space.id),
            this.getServiceOrdersCount(res.id)
          ]).pipe(
            map(otherDetails => {
              const response: DetailsResponse = {
                bookingDetails: res,
                reservationDetails: otherDetails[0],
                features: otherDetails[1],
                serviceOrdersCount: otherDetails[2],
              }
              this.detailsCache.set(id.toString(), response);
              return response;
            })
          )
        })
      );
    }
  }
  private getReservationDetails(reservationId: number, features?: string): Observable<ReservationDetails> {
    const url = `Reservations/${reservationId}${features ? `?reservationFeatureFlags=${features}` : ''}`;
    return this.dataContextService.getData(url).pipe(
      map(res => {
        if (res.status === 200) {
          const response = res.data as ReservationDetails;
          if (response.owner) {
            response.owner.firstName = this.dataContextService.getUserFirstName(response.owner);
            response.owner.lastName = response.owner.lastName ? response.owner.lastName : '';
          }
          if (response.pointOfContact) {
            response.pointOfContact.firstName = this.dataContextService.getUserFirstName(response.pointOfContact);
            response.pointOfContact.lastName = response.pointOfContact.lastName ? response.pointOfContact.lastName : '';
          }
          if (response.specialist) {
            response.specialist.firstName = this.dataContextService.getUserFirstName(response.specialist);
            response.specialist.lastName = response.specialist.lastName ? response.specialist.lastName : '';
          }
          return response;
        } else {
          this.dataContextService.showErrorToaster(res);
        }
      })
    );
  }

  getServiceOrdersCount(id: number, referenceType: ReferenceType = ReferenceType.Booking): Observable<ServiceOrdersCount> {
    const url = `ServiceOrders/Count/Booking/${referenceType}/${id.toString()}`;
    return this.dataContextService.getData(url).pipe(
      map(res => {
        if (res.status == 200) {
          return res.data as ServiceOrdersCount
        } else {
          this.dataContextService.showErrorToaster(res);
        }
      })
    );
  }

  getAuditTrail(input: AuditTrailInput): Observable<AuditTrailResponse> {
    let url = `AuditTrail?ReferenceType=Booking&`;
    if (input.id) {
      url += `Id=${input.id}&`;
    }
    if (input.limit) {
      url += `Limit=${input.limit}&`;
    }
    if (input.page) {
      url += `Page=${input.page}&`;
    }
    return this.dataContextService.getData(url).pipe(
      map(res => res.data as AuditTrailResponse),
      map(res => {
        return {
          ...res,
          items: res.items.map(i => {
            if (i.propertyName === BOOKING_AUDIT_TRAIL_PROPS.Status) {
              if (i.oldValue) {
                i.oldValue = DataContextService.BookingStatus.find(d => d.name === i.oldValue).displayName;
              }
              if (i.newValue) {
                i.newValue = DataContextService.BookingStatus.find(d => d.name === i.newValue).displayName;
              }
            }
            if (i.propertyName === BOOKING_AUDIT_TRAIL_PROPS.Setuptype) {
              if (i.oldValue) {
                i.oldValue = DataContextService.SpaceSetupTypes.find(d => d.id.toString() === i.oldValue).description;
              }
              if (i.newValue) {
                i.newValue = DataContextService.SpaceSetupTypes.find(d => d.id.toString() === i.newValue).description;
              }
            }
            if (i.propertyName === BOOKING_AUDIT_TRAIL_PROPS.Floor) {
              if (i.oldValue) {
                i.oldValue = i.oldValue.replace(/\bFloor\b/, this.dataContextService.translateText('Translation.Floor'));
              }
              if (i.newValue) {
                i.newValue = i.newValue.replace(/\bFloor\b/, this.dataContextService.translateText('Translation.Floor'));
              }
            }
            if (i.propertyName === BOOKING_AUDIT_TRAIL_PROPS.Statusreason) {
              const statusReasons = DataContextService.StatusReasons.find(i => i.id === 5).statusReasons;
              if (i.oldValue) {
                i.oldValue = statusReasons.find(r => r.id === Number(i.oldValue)).name;
              }
              if (i.newValue) {
                i.newValue = statusReasons.find(r => r.id === Number(i.newValue)).name;
              }
            }
            i.timeZoneAbbreviation = DataContextService.TimeZones.find(d => d.id === i.timeZoneId).abbreviation;
            i.changeDate = moment.parseZone(i.changeDate).format('l LT');
            i.propertyName = BOOKING_AUDIT_TRAIL_PROPS_NAMES.get(BOOKING_AUDIT_TRAIL_PROPS[i.propertyName.replace(/\s/g, '')]);
            return i;
          })
        };
      })
    );
  }

  getBookingTasksCount(reservationId: number): Observable<ReservationServiceOrderCount[]> {
    const url = `ServiceOrders/Tasks/Reservation/${reservationId}`;
    return this.dataContextService.getData(url).pipe(
      map(res => {
        if (res.status == 200) {
          return res.data as ReservationServiceOrderCount[]
        } else {
          this.dataContextService.showErrorToaster(res);
        }
      })
    )
  }
  editBooking(id: number, input: EditBooking) {
    let url = `SpaceBookings/${id}`;
    return this.dataContextService.putData(url, input).pipe(map(res => {
      if (res.status == 200) {
        return res.data
      } else {
        this.dataContextService.showErrorToaster(res);
      }
    }));
  }
  removeDetailsFromCache(id: number) {
    if (this.detailsCache.has(id.toString()))
      this.detailsCache.delete(id.toString());
  }
  updateCacheDetails(id: number, obj: DetailsResponse) {
    this.detailsCache.set(id.toString(), obj);
  }
  getDetailsFromCache(id: number): DetailsResponse {
    return this.detailsCache.get(id.toString());
  }
}
